import "reflect-metadata";
import { inject, injectable } from "inversify";
import api, { parseServerError, processBlobErrorResponse } from "@dropDesk/data/clients/http.client";
import { ListPaginationEntity } from "@dropDesk/domain/entities/common/list_pagination.entity";
import { TicketEntity } from "@dropDesk/domain/entities/ticket/ticket.entity";
import { apolloClient } from "@dropDesk/data/clients/apollo.client";
import { gql } from "@apollo/client";
import { Observable, from, Observer } from "rxjs";
import { mergeMap } from "rxjs/operators";
import { AxiosResponse, CancelTokenSource } from "axios";
import { ExportDataEntity } from "@dropDesk/domain/entities/export_data/export_data_entity";
import { ExportRemoteDataSource } from "@dropDesk/data/data_source/export/export_remote.datasource";
import { ConstantsKeys } from "@dropDesk/domain/entities/constants/constants_keys";
import { ChatRemoteDataSource } from "@dropDesk/data/data_source/chat/chat_remote_datasource";
import { getListSubscriptionResponse } from "@dropDesk/domain/entities/common/get_list_subscription_response";
import { TicketConfig } from "@dropDesk/domain/entities/ticket/ticket_config";
import { DropDeskErrorEnum } from "@dropDesk/domain/entities/exceptions/dropdesk_error_enum";
import { socket } from "@dropDesk/data/clients/socket.client";
import { SocketResponse, SocketResponseEntity } from "@dropDesk/domain/entities/chat/chat_socket";
import { TypeChatSocket } from "@dropDesk/domain/entities/chat/chat_enum";
import { BackupData } from "@dropDesk/domain/entities/ticket/backup/backup_data";
import { ExportTicketStatusEntity } from "@dropDesk/domain/entities/ticket/export_ticket_status.entity";

@injectable()
export abstract class TicketRemoteDataSource {

    public abstract set(ticket: TicketEntity): Promise<TicketEntity>;

    public abstract findByPK(id: string, cancelTokenSource?: CancelTokenSource): Promise<TicketEntity>;

    public abstract restore(tickets: TicketEntity[]): Promise<void>;

    public abstract delete(tickets: TicketEntity[]): Promise<void>;

    public abstract transfer(idTicket: string, newSectorId: string, newAttendantId?: string): Promise<TicketEntity>;

    public abstract listTicketsSubscription(): Observable<ListPaginationEntity<TicketEntity> | null>;

    public abstract changeTimeSpent(idTicket: string, timeSpent: number): Promise<TicketEntity>;

    public abstract changePriority(idTicket: string, priority: string): Promise<TicketEntity>;

    public abstract changeDateDue(idTicket: string, dateDue: string): Promise<TicketEntity>;

    public abstract changeProblemDescription(idTicket: string, description: string): Promise<TicketEntity>;

    public abstract cancel(idTicket: string, cancelDescription?: string): Promise<TicketEntity>;

    public abstract close(idTicket: string, closeDescription?: string): Promise<TicketEntity>;

    public abstract changeResolution(idTicket: string, closeDescription: string): Promise<TicketEntity>;

    public abstract linkAttendant(idTicket: string): Promise<TicketEntity>;

    public abstract exportSubscription(
        id: string,
        handleDisconnect: (temporary: boolean) => void,
        getLastUpdates: () => void,
        useSocket: boolean
    ): Observable<ExportDataEntity | null>;

    public abstract export(
        period: { startDate: string, endDate: string },
        idUser: string | null,
        idSector: string | null,
        idClient: string | null,
        id?: string,
    ): Promise<ExportDataEntity>;

    public abstract exportHTML(idTicket: string): Promise<{ success: boolean, data: ExportTicketStatusEntity }>;

    public abstract deleteExportHTML(): Promise<void>;

    public abstract getConfigs(): Promise<TicketConfig[]>;

    public abstract list(
        searchParam: string,
        page: number,
        limit: number,
        listOnlyDeleted: boolean,
        status: string | null,
        priority: string | null,
        listOnlySectorEmpty: boolean,
        cancelTokenSource?: CancelTokenSource,
        idClient?: string,
        idAttendant?: string,
        idUserClient?: string,
        idDescriptionStatic?: string,
        idSector?: string,
        gte?: string,
        lte?: string,
        bypassCount?: boolean,
    ): Promise<ListPaginationEntity<TicketEntity>>;

    public abstract listBackups(
        searchParam: string,
        page: number,
        limit: number,
    ): Promise<ListPaginationEntity<BackupData>>;

    public abstract startBackup(): Promise<BackupData>;

    public abstract getCurrentExportState(): Promise<ExportTicketStatusEntity | null>;

    public abstract cancelExport(id: string): Promise<void>;
}

@injectable()
export class TicketRemoteDataSourceImpl implements TicketRemoteDataSource {

    private _exportDataSource: ExportRemoteDataSource;
    private _chatRemoteDataSource: ChatRemoteDataSource;

    constructor(
        @inject(ExportRemoteDataSource) exportDataSource: ExportRemoteDataSource,
        @inject(ChatRemoteDataSource) chatRemoteDataSource: ChatRemoteDataSource
    ) {
        this._exportDataSource = exportDataSource;
        this._chatRemoteDataSource = chatRemoteDataSource;
    }

    baseTicketUrl: string = 'tickets/';
    baseRestoreTicketUrl: string = `${this.baseTicketUrl}restore/`;
    baseTransferUrl: string = `${this.baseTicketUrl}transfer/`;
    baseChangeTimeSpentUrl: string = `${this.baseTicketUrl}changeTimeSpent/`;
    baseChangePriorityUrl: string = `${this.baseTicketUrl}changePriority/`;
    baseChangeDateDutUrl: string = `${this.baseTicketUrl}changeDateDue/`;
    baseChangeProblemDescriptionUrl: string = `${this.baseTicketUrl}changeProblemDescription/`;
    baseCancelTicketUrl: string = `${this.baseTicketUrl}cancel/`;
    baseCloseTicketUrl: string = `${this.baseTicketUrl}close/`;
    baseChangeResolutionTicketUrl: string = `${this.baseTicketUrl}changeDescriptionClosed/`;
    baseLinkAttendantTicketUrl: string = `${this.baseTicketUrl}linkAttendant/`;
    baseUrlExport: string = `${this.baseTicketUrl}export/start`;
    baseUrlExportHTML: string = `${this.baseTicketUrl}export/html`;
    baseUrlDeleteExportHTML: string = `${this.baseTicketUrl}export/current-html-state`;
    baseUrlCurrentExportHTML: string = `${this.baseTicketUrl}export/current-html-state`;
    baseUrlCancelExportHTML: string = `${this.baseTicketUrl}export/cancel-html`;
    baseUrlBackupList: string = `${this.baseTicketUrl}listBackups`;
    baseUrlStartBackup: string = `${this.baseTicketUrl}startBackup`;

    public linkAttendant(idTicket: string): Promise<TicketEntity> {
        return new Promise<TicketEntity>(async (resolve, reject) => {
            try {
                const response = await api.patch(`${this.baseLinkAttendantTicketUrl}${idTicket}`);
                return resolve(TicketEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public startBackup(): Promise<BackupData> {
        return new Promise<BackupData>(async (resolve, reject) => {
            try {
                const response = await api.get(`${this.baseUrlStartBackup}`);
                return resolve(BackupData.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public close(idTicket: string, closeDescription?: string): Promise<TicketEntity> {
        return new Promise<TicketEntity>(async (resolve, reject) => {
            try {
                const response = await api.patch(`${this.baseCloseTicketUrl}?idTicket=${idTicket}${closeDescription ? `&closeDescription=${encodeURIComponent(closeDescription)}` : ''}`);
                return resolve(TicketEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public changeResolution(idTicket: string, closeDescription: string): Promise<TicketEntity> {
        return new Promise<TicketEntity>(async (resolve, reject) => {
            try {
                const response = await api.patch(`${this.baseChangeResolutionTicketUrl}${idTicket}/${encodeURIComponent(closeDescription)}`);
                return resolve(TicketEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }


    public cancel(idTicket: string, cancelDescription?: string): Promise<TicketEntity> {
        return new Promise<TicketEntity>(async (resolve, reject) => {
            try {
                const response = await api.patch(`${this.baseCancelTicketUrl}${idTicket}/${encodeURIComponent(cancelDescription ?? '')}`);
                return resolve(TicketEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public changeProblemDescription(idTicket: string, description: string): Promise<TicketEntity> {
        return new Promise<TicketEntity>(async (resolve, reject) => {
            try {
                const response = await api.patch(this.baseChangeProblemDescriptionUrl + idTicket + `/${encodeURIComponent(description)}`);
                return resolve(TicketEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public changeDateDue(idTicket: string, dateDue: string): Promise<TicketEntity> {
        return new Promise<TicketEntity>(async (resolve, reject) => {
            try {
                const response = await api.patch(this.baseChangeDateDutUrl + idTicket + `/${dateDue}`);
                return resolve(TicketEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public changePriority(idTicket: string, priority: string): Promise<TicketEntity> {
        return new Promise<TicketEntity>(async (resolve, reject) => {
            try {
                const response = await api.patch(this.baseChangePriorityUrl + idTicket + `/${priority}`);
                return resolve(TicketEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public changeTimeSpent(idTicket: string, timeSpent: number): Promise<TicketEntity> {
        return new Promise<TicketEntity>(async (resolve, reject) => {
            try {
                const response = await api.patch(this.baseChangeTimeSpentUrl + idTicket + `/${timeSpent}`);
                return resolve(TicketEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }


    public transfer(idTicket: string, newSectorId: string, newAttendantId?: string): Promise<TicketEntity> {
        return new Promise<TicketEntity>(async (resolve, reject) => {
            try {
                const response = await api.patch(this.baseTransferUrl + idTicket + `/${newSectorId}` + `${newAttendantId ? `/${newAttendantId}` : ''}`);
                return resolve(TicketEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    private getTicketsByIds(records: Record<string, any>[]): Promise<TicketEntity[]> {
        return new Promise<TicketEntity[]>(async (resolve, reject) => {
            try {
                const ids: string[] = [];
                const cache: TicketEntity[] = [];
                for (const record of records) {
                    if (this.hasValidCache(record)) {
                        const ticket = this.getFromCache(record['id']);
                        if (ticket) {
                            cache.push(ticket);
                        }
                    } else {
                        ids.push(record['id']);
                    }
                }
                let result: TicketEntity[] = [];
                if (ids.length > 0) {
                    const response = await api.get(`${this.baseTicketUrl}byIds/${ids.map((entry) => entry).join(',')}`);
                    result = (response.data as Record<string, any>[]).map(ticket => TicketEntity.fromJson(ticket));
                }
                const resolvedList = [...result, ...cache];
                let sortFn = (ticket1: TicketEntity, ticket2: TicketEntity) => ticket1.number > ticket2.number ? -1 : ticket1.number < ticket2.number ? 1 : 0
                const sortedList = resolvedList.sort(sortFn);
                this.persistCache(sortedList)
                return resolve(sortedList);
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    private clearCache(): void {
        localStorage.removeItem('tickets');
    }

    private persistCache(tickets: TicketEntity[]): void {
        localStorage.setItem('tickets', JSON.stringify(tickets));
    }

    private getCache(): TicketEntity[] {
        const cache = localStorage.getItem('tickets');
        if (cache) {
            return (JSON.parse(cache) as Record<string, any>[]).map(ticket => TicketEntity.fromJson(ticket));
        }

        return [];
    }

    private hasValidCache(record: Record<string, any>): boolean {
        let result = false;
        const tickets = this.getCache();
        for (const ticket of tickets) {
            if (ticket.id === record['id'] && ticket.updatedAt === record['updatedAt']) {
                result = true;
            }
        }

        return result;
    }

    private getFromCache(id: string): TicketEntity | null {
        let result: TicketEntity | null = null;
        const tickets = this.getCache();
        for (const ticket of tickets) {
            if (ticket.id === id) {
                result = ticket;
            }
        }

        return result;
    }

    private listTicketsSubscriptionSocket(): Observable<ListPaginationEntity<TicketEntity> | null> {
        return new Observable<ListPaginationEntity<TicketEntity> | null>((observer: Observer<ListPaginationEntity<TicketEntity> | null>) => {
            if (!socket) {
                console.error('Socket not connected');
                return;
            }

            const handleTicketListMessage = async (response: any) => {
                try {
                    const result = SocketResponse.fromJson(new SocketResponseEntity(response));

                    if (result.isInformRooms || result.hasUnknownError || result.type !== TypeChatSocket.ticket) {
                        return;
                    }

                    const tickets = result.tickets ?? [];
                    if (!tickets.length) {
                        return;
                    }

                    const totalRows = tickets.length;
                    const limit = tickets.length;
                    const ticketsPaginated = new ListPaginationEntity<TicketEntity>({
                        data: tickets,
                        page: 0,
                        limit,
                        totalRows,
                        pages: Math.ceil(totalRows / limit),
                    });
                    observer.next(ticketsPaginated);
                } catch (error) {
                    console.error(error);
                }
            };

            socket!.on('message', handleTicketListMessage);
            return () => {
                socket?.off('message', handleTicketListMessage);
            };
        });
    }

    public listTicketsSubscription(): Observable<ListPaginationEntity<TicketEntity> | null> {
        return this.listTicketsSubscriptionSocket();
    }

    public list(
        searchParam: string,
        page: number,
        limit: number,
        listOnlyDeleted: boolean,
        status: string | null,
        priority: string | null,
        listOnlySectorEmpty: boolean,
        cancelTokenSource?: CancelTokenSource,
        idClient?: string,
        idAttendant?: string,
        idUserClient?: string,
        idDescriptionStatic?: string,
        idSector?: string,
        gte?: string,
        lte?: string,
        bypassCount?: boolean,
    ): Promise<ListPaginationEntity<TicketEntity>> {
        return new Promise<ListPaginationEntity<TicketEntity>>(async (resolve, reject) => {
            try {
                const response = await api.get(
                    `${this.baseTicketUrl}?searchParam=${searchParam ? encodeURIComponent(searchParam) : ''}&page=${page}&limit=${limit}&type=${listOnlyDeleted ? 'deleted' : ''}&status=${status ?? ''}&priority=${priority ?? ''}&listOnlySectorEmpty=${listOnlySectorEmpty}&idClient=${idClient ?? ''}&idAttendant=${idAttendant ?? ''}&idUserClient=${idUserClient ?? ''}&idDescriptionStatic=${idDescriptionStatic ?? ''}&idSector=${idSector ?? ''}&gte=${gte ?? ''}&lte=${lte ?? ''}${bypassCount ? '&bypassCount=true' : ''}`,
                    { cancelToken: cancelTokenSource?.token }
                );
                const data: TicketEntity[] = response.data.data.map((entry: TicketEntity) => TicketEntity.fromJson(entry));
                const result = new ListPaginationEntity<TicketEntity>({
                    page: response.data.page,
                    limit: response.data.limit,
                    totalRows: response.data.totalRows,
                    hasMore: response.data.page < (response.data.pages - 1),
                    pages: response.data.pages,
                    data,
                });
                return resolve(result);
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public listBackups(
        searchParam: string,
        page: number,
        limit: number,
    ): Promise<ListPaginationEntity<BackupData>> {
        return new Promise<ListPaginationEntity<BackupData>>(async (resolve, reject) => {
            try {
                const response = await api.get(
                    `${this.baseUrlBackupList}?searchParam=${searchParam ? encodeURIComponent(searchParam) : ''}&page=${page}&limit=${limit}}`
                );
                const data: BackupData[] = response.data.data.map((entry: BackupData) => BackupData.fromJson(entry));
                const result = new ListPaginationEntity<BackupData>({
                    page: response.data.page,
                    limit: response.data.limit,
                    totalRows: response.data.totalRows,
                    hasMore: response.data.page < (response.data.pages - 1),
                    pages: response.data.pages,
                    data,
                });
                return resolve(result);
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }


    public set(ticket: TicketEntity): Promise<TicketEntity> {
        return new Promise<TicketEntity>(async (resolve, reject) => {
            try {
                const response = await api.post(this.baseTicketUrl, ticket.toJson());
                return resolve(TicketEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public restore(tickets: TicketEntity[]): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {

                for (let i = 0; i < tickets.length; i++) {
                    await api.patch(this.baseRestoreTicketUrl + tickets[i].id);
                }

                return resolve();

            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public delete(tickets: TicketEntity[]): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {

                for (let i = 0; i < tickets.length; i++) {
                    await api.delete(this.baseTicketUrl + tickets[i].id);
                }

                return resolve();

            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public findByPK(id: string, cancelTokenSource?: CancelTokenSource): Promise<TicketEntity> {
        return new Promise<TicketEntity>(async (resolve, reject) => {
            try {
                const url: string = `${this.baseTicketUrl}${id}?limit=${ConstantsKeys.defaultLimit}`;
                const response = await api.get(url, { cancelToken: cancelTokenSource?.token });
                const ticket = TicketEntity.fromJson(response.data);
                return resolve(ticket);
            } catch (error) {
                const errorParsed = parseServerError(error);
                if (errorParsed.message === DropDeskErrorEnum.operationCanceledDueToNewRequest) {
                    return;
                }
                return reject(errorParsed);
            }
        })
    }

    public export(
        period: { startDate: string, endDate: string },
        idUser: string | null,
        idSector: string | null,
        idClient: string | null,
        id?: string,
    ): Promise<ExportDataEntity> {
        return new Promise<ExportDataEntity>(async (resolve, reject) => {
            try {
                const response = await api.get(`${this.baseUrlExport}?gte=${period.startDate}&lte=${period.endDate}&id=${id ?? ''}&idUser=${idUser ?? ''}&idSector=${idSector ?? ''}&idClient=${idClient ?? ''}`);
                const exportData = new ExportDataEntity({
                    ...response.data,
                    progress: parseFloat((response.data.progress * 100).toFixed(2))
                });
                return resolve(exportData);

            } catch (error: any) {
                return reject(parseServerError(error));
            }
        })
    }

    async exportHTML(idTicket: string): Promise<{ success: boolean, data: ExportTicketStatusEntity }> {
        return new Promise<{ success: boolean, data: ExportTicketStatusEntity }>(async (resolve, reject) => {
            try {
                const result = await this.getCurrentExportState();
                if (result && result.inProgress) {
                    return resolve({ success: false, data: ExportTicketStatusEntity.fromJson(result) });
                }
                const url = `${this.baseUrlExportHTML}?id=${idTicket}`;
                const response = await api.get(url);
                return resolve({ success: true, data: ExportTicketStatusEntity.fromJson(response.data) });
            } catch (error: any) {
                return reject(parseServerError(error));
            }
        });
    }

    async deleteExportHTML(): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                await api.delete(this.baseUrlDeleteExportHTML);
                return resolve();
            } catch (error: any) {
                return reject(parseServerError(error));
            }
        });
    }

    async getCurrentExportState(): Promise<ExportTicketStatusEntity | null> {
        return new Promise<ExportTicketStatusEntity | null>(async (resolve, reject) => {
            try {
                const response = await api.get(this.baseUrlCurrentExportHTML);
                return resolve(response.data ? ExportTicketStatusEntity.fromJson(response.data) : null);
            } catch (error: any) {
                return reject(parseServerError(error));
            }
        });
    }

    public exportSubscription(
        id: string,
        handleDisconnect: (temporary: boolean) => void,
        getLastUpdates: () => void,
        useSocket: boolean
    ): Observable<ExportDataEntity | null> {
        return new Observable<ExportDataEntity | null>((observer: Observer<ExportDataEntity | null>) => {
            return this._exportDataSource.exportSubscription(
                'tickets',
                id,
                handleDisconnect,
                getLastUpdates,
                useSocket
            ).subscribe({
                next: (response) => {
                    observer.next(response);
                },
                error(err: any) {
                    observer.error(err);
                }
            })
        });
    }

    getConfigs(): Promise<TicketConfig[]> {
        return new Promise<TicketConfig[]>(async (resolve, reject) => {
            try {
                const url: string = `${this.baseTicketUrl}getGlobalConfigs`;
                const response = await api.get(url);
                const configs = (response.data as Record<string, any>[]).map((json) => TicketConfig.fromJson(json));
                return resolve(configs);
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    async cancelExport(id: string): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                await api.delete(`${this.baseUrlCancelExportHTML}/${id}`);
                return resolve();
            } catch (error: any) {
                return reject(parseServerError(error));
            }
        });
    }

}
